import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const EMPTY = { name: "", email: "", business: "", brief: "", website: "" };
const fieldCls = "mt-1.5 rounded-none bg-white border-black/15 focus-visible:ring-2 focus-visible:ring-[#ED5A24]";

export const EnquiryDialog = ({ open, onOpenChange }) => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/enquiries", form);
      setSent(true);
      setForm(EMPTY);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send enquiry");
    } finally {
      setBusy(false);
    }
  };

  const close = (v) => {
    onOpenChange(v);
    if (!v) setSent(false);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="rounded-none sm:max-w-lg" data-testid="enquiry-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl tracking-tight font-bold">Custom logo enquiry</DialogTitle>
          <DialogDescription>Tell the Get Set Design team about your brand. We'll reply by email.</DialogDescription>
        </DialogHeader>
        {sent ? (
          <div className="py-6" data-testid="enquiry-success">
            <p className="text-lg font-bold">Thanks — your brief is in.</p>
            <p className="text-sm text-[#52525B] mt-2">The Get Set Design team will be in touch soon.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="enquiry-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="enq-name" className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#52525B]">Name</Label>
                <Input id="enq-name" required maxLength={120} value={form.name} onChange={set("name")} className={fieldCls} data-testid="enquiry-name-input" />
              </div>
              <div>
                <Label htmlFor="enq-email" className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#52525B]">Email</Label>
                <Input id="enq-email" type="email" required value={form.email} onChange={set("email")} className={fieldCls} data-testid="enquiry-email-input" />
              </div>
            </div>
            <div>
              <Label htmlFor="enq-business" className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#52525B]">Business name (optional)</Label>
              <Input id="enq-business" maxLength={160} value={form.business} onChange={set("business")} className={fieldCls} data-testid="enquiry-business-input" />
            </div>
            <div>
              <Label htmlFor="enq-brief" className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#52525B]">Your brief</Label>
              <Textarea
                id="enq-brief" required minLength={10} maxLength={4000} rows={5} value={form.brief} onChange={set("brief")}
                placeholder="What does your business do, what style do you like, any deadline?"
                className={fieldCls} data-testid="enquiry-brief-input"
              />
            </div>
            <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} className="hidden" aria-hidden="true" />
            <button
              type="submit"
              disabled={busy}
              data-testid="enquiry-submit-btn"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#ED5A24] hover:bg-[#0A0A0B] text-white text-xs uppercase tracking-[0.2em] font-bold h-12 transition-colors duration-200 disabled:opacity-60"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send enquiry
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
